import { ComponentProps, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AgentHandoff,
  buildHandoffTool,
  HandoffAgentId
} from '@blisspointmedia/bpm-types/dist/agents';
import { AgentRoute, AgentSessionManager } from '@Components/AgentChat/AgentSessionManager';
import { AgentMessage, MessageRole } from '@Components/AgentChat/agent-chat-types';
import { AgentResponse } from '../../utils/agent-client';
import { Agent, AgentType, useAgentEndpoint } from '../../utils/agent-endpoints';
import { ChartHandle } from '../Charts/chart-handle';
import {
  analysisResultConfig,
  executedAnalysisConfig,
  findHandoffCall,
  isHandoffAgentId,
  messageLabelFor,
  planHandoff,
  resolveChartConfigs
} from './agent-dispatch-utils';

type DispatcherProps = Pick<
  ComponentProps<typeof AgentSessionManager>,
  'routeTurn' | 'resolveHandoff' | 'messageLabel'
>;

interface UseAgentDispatcherArgs {
  company: string;
  // The charts the dashboard agent can name in a handoff (the focused chart, or the dashboard's).
  focusedChartIds: ChartHandle[];
}

// The router seam for the combined agent chat: picks which agent runs each turn, offers it the
// `handoff` tool, and turns a handoff call into the next run. A router runtime can replace this later
// without the agents changing.
export const useAgentDispatcher = ({
  company,
  focusedChartIds
}: UseAgentDispatcherArgs): DispatcherProps => {
  const queryClient = useQueryClient();
  // Both up front: hooks can't be conditional.
  const dashboardEndpoint = useAgentEndpoint(Agent.DASHBOARD_CHARTS);
  const dataAnalysisEndpoint = useAgentEndpoint(Agent.DATA_ANALYSIS);

  // Only the dashboard agent is given the focused chart ids. Data analysis gets none, so it's offered
  // `resultId` (one of its own query results), never the chart that happens to still be focused.
  const routeFor = useCallback(
    (agent: HandoffAgentId): AgentRoute => ({
      agent,
      endpoint: agent === Agent.DASHBOARD_CHARTS ? dashboardEndpoint : dataAnalysisEndpoint,
      tools: [buildHandoffTool(agent, agent === Agent.DASHBOARD_CHARTS ? focusedChartIds : [])]
    }),
    [dashboardEndpoint, dataAnalysisEndpoint, focusedChartIds]
  );

  const routeTurn = useCallback(
    (activeAgent: string | undefined) =>
      routeFor(isHandoffAgentId(activeAgent) ? activeAgent : Agent.DASHBOARD_CHARTS),
    [routeFor]
  );

  const resolveHandoff = useCallback(
    (
      response: AgentResponse,
      routedAgent: AgentType,
      isFollowUp: boolean,
      sentHandoff?: AgentHandoff,
      threadMessages: AgentMessage[] = []
    ) => {
      const call = findHandoffCall(response.segments);
      if (!call || !isHandoffAgentId(routedAgent)) return null;
      const { note, target, handoff } = planHandoff({
        args: call.args,
        from: routedAgent,
        isFollowUp,
        resolveChart: chartId => resolveChartConfigs(queryClient, company, chartId),
        resolveResult: resultId =>
          analysisResultConfig(
            [
              ...threadMessages.flatMap(message =>
                message.role === MessageRole.ASSISTANT && message.agent === Agent.DATA_ANALYSIS
                  ? message.segments
                  : []
              ),
              ...response.segments
            ],
            resultId
          ),
        ...(routedAgent === Agent.DATA_ANALYSIS && {
          executedConfig: executedAnalysisConfig(response.segments, sentHandoff)
        })
      });
      return target && handoff ? { note, followUp: { ...routeFor(target), handoff } } : { note };
    },
    [company, queryClient, routeFor]
  );

  return { routeTurn, resolveHandoff, messageLabel: messageLabelFor };
};
